import { useEffect, useState } from 'react';
import type { Resource } from '../../types/resource';
import { useResourceViewerStore } from '../../stores/resourceViewerStore';
import { useResourceChildren } from '../../hooks/useResource';
import { useProject } from '../../hooks/useProject';
import { hasPermission } from '../../types/project';
import { PaginationControls } from '../PaginationControls';
import { ResourceRow } from '../ResourceRow';
import RelatedBulkTag from './RelatedBulkTag';

const PAGE_SIZE = 20;

export default function RelatedTab({ resource }: { resource: Resource }) {
  const open = useResourceViewerStore((s) => s.open);
  const [childrenPage, setChildrenPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: project } = useProject(resource.projectId);
  const canManageTags = !!project && hasPermission(project, 'MANAGE_TAGS');

  // Reset page + selection when the open document changes (viewer prev/next).
  useEffect(() => {
    setSelected(new Set());
    setChildrenPage(0);
  }, [resource.id]);

  const { data: childrenData } = useResourceChildren(
    resource.projectId,
    resource.collectionId,
    resource.id,
    childrenPage,
    PAGE_SIZE
  );

  function openResource(r: Resource) {
    open({ projectId: r.projectId, collectionId: r.collectionId, resourceId: r.id });
  }

  function toggleSelect(r: { id: string }) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(r.id) ? next.delete(r.id) : next.add(r.id);
      return next;
    });
  }

  const children = childrenData?.resources ?? resource.children;
  const totalChildren = childrenData?.totalResources ?? resource.children.length;
  const totalPages = Math.max(1, Math.ceil(totalChildren / PAGE_SIZE));

  return (
    <div style={{ overflowY: 'auto', maxHeight: '100%', paddingTop: 10 }}>
      {canManageTags && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 46, padding: '0 17px' }}>
          <RelatedBulkTag
            projectId={resource.projectId}
            selectedIds={[...selected]}
            onTagged={() => setSelected(new Set())}
          />
        </div>
      )}

      <h4 style={{ padding: '0 10px' }}>Parents</h4>
      {resource.parents.length === 0 ? (
        <div className="alert alert-info alert-block">
          <h4>No parent found</h4>
          <p>This document does not belong to another document</p>
        </div>
      ) : (
        <div className="iio-resource-medias">
          {resource.parents.map((p) => (
            <ResourceRow
              key={p.id}
              resource={p}
              onClick={openResource}
              noIndent
              selectable={canManageTags}
              selected={selected.has(p.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      <h4 style={{ padding: '0 10px' }}>Children</h4>
      {resource.children.length === 0 ? (
        <div className="alert alert-info alert-block">
          <h4>No children found</h4>
          <p>This document did not have other documents contained within it</p>
        </div>
      ) : (
        <>
          <PaginationControls
            currentPage={childrenPage}
            totalPages={totalPages}
            onGoTo={setChildrenPage}
          />
          <div className="iio-resource-medias">
            {children.map((c) => (
              <ResourceRow
                key={c.id}
                resource={c}
                onClick={openResource}
                noIndent
                selectable={canManageTags}
                selected={selected.has(c.id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </div>
          <PaginationControls
            currentPage={childrenPage}
            totalPages={totalPages}
            onGoTo={setChildrenPage}
          />
        </>
      )}
    </div>
  );
}
